"use client";
import React, { useContext, useEffect } from "react";
import { Text } from "rizzui/typography";
import FormBox from "./fromBox";
import { Controller, useForm } from "react-hook-form";
import { Input } from "rizzui/input";
import { Textarea } from "rizzui/textarea";
import { Button } from "rizzui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { baseUrl } from "../../account/login";
import { AuthContext } from "../../../context/authContext";
import CryptoJS from "crypto-js";
import { useNavigate } from "react-router";
import Select from "react-select";
import { useColorStyle } from "../../../hooks/useColorStyle";
import { ThemeContext } from "../../../context/themeContext";

export const projectFieldValidation = {
  required: {
    value: true,
    message: "is required",
  },
};

export const inputClassName =
  "hover:border-muted border border-muted font-switzer text-[12px] rounded-[4px] font-[400] text-primary-dark [&.is-focus]:ring-[0px] ring-[0px] [&.is-hover]:border-muted [&.is-focus]:border-muted h-[35px]";

export const textAreaClassName =
  "hover:border-muted border border-muted font-switzer text-[12px] rounded-[4px] font-[400] text-primary-dark [&.is-focus]:ring-[0px] ring-[0px] [&.is-hover]:border-muted [&.is-focus]:border-muted resize-none";
export const labelClassName =
  "font-[400] font-switzer text-[12px] leading-[15.34px] text-[#758599]";

export default function First({ data }) {
  const { page, setPage, campaign } = data;

  const campaign_type_options = [
    { label: "Sales Qualification", value: "Sales Qualification" },
    { label: "Appointment Booking", value: "Appointment Booking" },
    { label: "Engagement", value: "Engagement" },
    { label: "Reactivation", value: "Reactivation" },
    { label: "Upselling", value: "Upselling" },
    {
      label: "Customer and Product Research",
      value: "Customer and Product Research",
    },
  ];

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    control,
    reset,
  } = useForm({
    defaultValues: {
      campaign_name: campaign?.campaign_name || "",
      campaign_description: campaign?.campaign_description || "",
      about_business: campaign?.about_business || "",
      campaign_type:
        campaign_type_options.find(
          (type) => type?.value === campaign?.campaign_type
        ) || null,
    },
  });

  const { token, profile } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const { theme } = useContext(ThemeContext);
  useEffect(() => {
    if (profile?.business_about) {
      const currentValues = getValues();
      reset({
        ...currentValues,
        about_business: profile.business_about,
      });
    }
  }, [profile]);

  const { campaignColorStyles: colorStyles } = useColorStyle(theme);
  const navigate = useNavigate();
  const key = process.env.REACT_APP_SECRET_KEY;
  const queryClient = useQueryClient();

  const newCampaign = () => {
    const {
      campaign_name,
      campaign_type,
      campaign_description,
      about_business,
    } = getValues();
    return axios
      .post(
        `${baseUrl}/campaigns`,
        {
          campaign_name,
          campaign_type: campaign_type?.value,
          campaign_description,
          about_business,
        },
        { headers: { Authorization: AuthStr } }
      )
      .then((response) => {
        const data = response.data;
        if (response?.data?.success) {
          const newpage = page + 1;
          const encryptedPage = CryptoJS.AES.encrypt(
            JSON.stringify(newpage),
            key!
          ).toString();
          sessionStorage.setItem("new_campaign_page", encryptedPage);
          sessionStorage.setItem("campaign_id", data?.data?._id);
          setPage(page + 1);
        }
        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.statusCode === 401) navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };

  const editCampaign = () => {
    const {
      campaign_name,
      campaign_type,
      campaign_description,
      about_business,
    } = getValues();
    return axios
      .put(
        `${baseUrl}/campaigns/${campaign?._id}`,
        {
          campaign_name,
          campaign_type: campaign_type?.value,
          campaign_description,
          about_business,
        },
        { headers: { Authorization: AuthStr } }
      )
      .then((response) => {
        const data = response.data;
        if (response?.data?.success) {
          const newpage = page + 1;
          const encryptedPage = CryptoJS.AES.encrypt(
            JSON.stringify(newpage),
            key!
          ).toString();
          sessionStorage.setItem("new_campaign_page", encryptedPage);
          sessionStorage.setItem("campaign_id", data?.data?._id);
          queryClient.invalidateQueries({ queryKey: [campaign?._id, token] });
          setPage(page + 1);
        }
        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.statusCode === 401) navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };

  /*   useEffect(() => {
    const campaign_type = campaign_type_options?.find(
      (type) => type?.value === campaign?.campaign_type
    );
    const campaign_name = campaign?.campaign_name;
    const campaign_description = campaign?.campaign_description;
    const about_business = campaign?.about_business;

    if (campaign) {
      reset({
        campaign_description: campaign_description || "",
        campaign_type: campaign_type || null,
        campaign_name: campaign_name || "",
        about_business: about_business || "",
      });
    }
  }, [campaign]); */

  const {
    isLoading,
    error,
    data: response,
    refetch,
  } = useQuery({
    queryKey: ["new_campaign_details", token, key],
    queryFn: () => (campaign ? editCampaign() : newCampaign()),
    enabled: false,
    retry: 0,
  });

  const handleNext = handleSubmit((data) => {
    refetch();
  });

  return (
    <FormBox
      title={"Campaign details"}
      subtitle={"Input the basic info about this campaign"}
      containerClass={
        "w-full text-secondary-dark font-switzer text-[12px] font-[400] leading-[15.84px]"
      }
      parentClass={"self-start md:w-[460px]"}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleNext();
        }}
        className="flex flex-col gap-[16px]"
        noValidate
      >
        <Controller
          control={control}
          name="campaign_name"
          rules={projectFieldValidation}
          render={({ field }) => (
            <Input
              type="text"
              label="Campaign name*"
              variant="text"
              rounded="md"
              labelClassName={labelClassName}
              inputClassName={inputClassName}
              {...field}
              error={
                errors.campaign_name?.message
                  ? "Campaign name " + errors.campaign_name.message
                  : ""
              }
            />
          )}
        />

        <Controller
          control={control}
          name="campaign_type"
          rules={{
            required: {
              value: true,
              message: "Campaign type is required",
            },
          }}
          render={({ field: { onChange, value, name } }) => (
            <div>
              <Text className={`${labelClassName} mb-[5px]`}>
                Campaign type*
              </Text>
              <Select
                options={campaign_type_options}
                placeholder=""
                styles={colorStyles}
                value={value}
                name={name}
                onChange={(e) => {
                  onChange(e);
                }}
              />
              {errors.campaign_type?.message && (
                <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                  {"" + errors.campaign_type.message}
                </p>
              )}
            </div>
          )}
        />

        <Controller
          control={control}
          name="campaign_description"
          rules={projectFieldValidation}
          render={({ field }) => (
            <Textarea
              label="Description*"
              variant="outline"
              rounded="md"
              rows={3}
              textareaClassName={textAreaClassName}
              labelClassName={labelClassName}
              {...field}
              style={{ resize: "none" }}
              error={
                errors.campaign_description?.message
                  ? "Campaign description " +
                    errors.campaign_description.message
                  : ""
              }
            />
          )}
        />

        <Textarea
          label="About your business*"
          variant="outline"
          rounded="md"
          rows={3}
          textareaClassName={textAreaClassName}
          labelClassName={labelClassName}
          {...register("about_business", {
            required: {
              value: true,
              message: "Tell us about your business",
            },
          })}
          style={{ resize: "none" }}
          error={
            errors.about_business?.message
              ? " " + errors.about_business.message
              : ""
          }
        />

        <Button
          type="submit"
          className="h-[40px] rounded-[4px] bg-btn hover:!bg-btn font-[500] font-switzer text-[12px] leading-[15.84px] text-background"
          isLoading={isLoading}
        >
          Continue
        </Button>
      </form>
      {error && (
        <Text className="text-red-400 font-switzer mt-2 text-[12px] font-[400]">
          {error.message}
        </Text>
      )}
    </FormBox>
  );
}
